import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  createHealthHouseConnector,
  deleteHealthHouseConnector,
  listHealthHouseConnectors,
  patchHealthHouseConnector,
  rotateHealthHouseConnectorToken
} from '../api/healthHouseConnectorsApi';
import './HealthHouseConnectors.css';

const DialogPortal = ({ children }) => createPortal(children, document.body);
const emptyForm = { code: '', name: '', internal: false, tokenMode: 'auto', token: '' };
const codePattern = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const generatedTokenPattern = /^hhc_v1_[A-Za-z0-9_-]{43}$/;
const customTokenPattern = /^[A-Za-z0-9][A-Za-z0-9._-]{7,127}$/;
const isValidToken = (token) => (
  token.startsWith('hhc_') ? generatedTokenPattern.test(token) : customTokenPattern.test(token)
);

const getRequestErrorMessage = (error, fallback) => (
  error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback
);

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).format(parsed);
};

const HealthHouseConnectors = () => {
  const { t } = useTranslation();
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formDialog, setFormDialog] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [tokenDialog, setTokenDialog] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [secret, setSecret] = useState(null);
  const [copyState, setCopyState] = useState('');

  const loadConnectors = useCallback(async (silent = false) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const data = await listHealthHouseConnectors();
      setConnectors(Array.isArray(data) ? data : []);
      setError('');
    } catch (requestError) {
      setError(getRequestErrorMessage(requestError, t('healthHouseConnectors.fetchError')));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    loadConnectors();
    const intervalId = window.setInterval(() => loadConnectors(true), 15000);
    return () => window.clearInterval(intervalId);
  }, [loadConnectors]);

  const counts = useMemo(() => connectors.reduce((summary, item) => {
    if (item.enabled === false) {
      summary.disabled += 1;
    } else if (item.online) {
      summary.online += 1;
    } else {
      summary.offline += 1;
    }
    return summary;
  }, { online: 0, offline: 0, disabled: 0 }), [connectors]);

  const visibleConnectors = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return connectors.filter((item) => {
      const state = item.enabled === false ? 'disabled' : (item.online ? 'online' : 'offline');
      if (statusFilter !== 'all' && statusFilter !== state) {
        return false;
      }
      if (!normalizedQuery) {
        return true;
      }
      return [item.name, item.code, item.token_hint]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedQuery));
    });
  }, [connectors, query, statusFilter]);

  const beginCreate = () => {
    setError('');
    setNotice('');
    setForm(emptyForm);
    setFormDialog({ mode: 'create' });
  };

  const beginEdit = (item) => {
    setError('');
    setNotice('');
    setForm({ ...emptyForm, code: item.code, name: item.name, internal: item.internal === true });
    setFormDialog({ mode: 'edit', connector: item });
  };

  const closeForm = () => {
    if (saving) {
      return;
    }
    setFormDialog(null);
    setForm(emptyForm);
    setError('');
  };

  const validateForm = () => {
    const normalizedName = form.name.trim();
    if (!normalizedName || normalizedName.length > 128) {
      setError(t('healthHouseConnectors.invalidName'));
      return false;
    }
    if (formDialog?.mode === 'create') {
      const normalizedCode = form.code.trim().toLowerCase();
      if (!codePattern.test(normalizedCode)) {
        setError(t('healthHouseConnectors.invalidCode'));
        return false;
      }
      if (form.internal && form.tokenMode === 'custom' && !isValidToken(form.token)) {
        setError(t('healthHouseConnectors.invalidToken'));
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (formDialog.mode === 'create') {
        const payload = {
          code: form.code.trim().toLowerCase(),
          name: form.name.trim(),
          internal: form.internal,
          ...(form.internal && form.tokenMode === 'custom' ? { token: form.token } : {})
        };
        const result = await createHealthHouseConnector(payload);
        setFormDialog(null);
        setForm(emptyForm);
        setNotice(t('healthHouseConnectors.createSuccess'));
        setCopyState('');
        setSecret({
          token: result.token,
          code: result.connector?.code || payload.code
        });
      } else {
        await patchHealthHouseConnector(formDialog.connector.id, { name: form.name.trim() });
        setFormDialog(null);
        setForm(emptyForm);
        setNotice(t('healthHouseConnectors.updateSuccess'));
      }
      await loadConnectors(true);
    } catch (requestError) {
      const fallback = requestError?.response?.status === 409
        ? t('healthHouseConnectors.credentialConflict')
        : t('healthHouseConnectors.saveError');
      setError(getRequestErrorMessage(requestError, fallback));
    } finally {
      setSaving(false);
    }
  };

  const enableConnector = async (item) => {
    setBusyId(item.id);
    setError('');
    try {
      await patchHealthHouseConnector(item.id, { enabled: true });
      setNotice(t('healthHouseConnectors.enableSuccess'));
      await loadConnectors(true);
    } catch (requestError) {
      setError(getRequestErrorMessage(requestError, t('healthHouseConnectors.saveError')));
    } finally {
      setBusyId(null);
    }
  };

  const runConfirmedAction = async () => {
    const action = confirmAction;
    if (!action) {
      return;
    }
    setConfirmAction(null);
    setBusyId(action.connector.id);
    setError('');
    try {
      if (action.type === 'delete') {
        await deleteHealthHouseConnector(action.connector.id);
        setNotice(t('healthHouseConnectors.deleteSuccess'));
      } else if (action.type === 'disable') {
        await patchHealthHouseConnector(action.connector.id, { enabled: false });
        setNotice(t('healthHouseConnectors.disableSuccess'));
      } else if (action.type === 'mark-internal') {
        await patchHealthHouseConnector(action.connector.id, { internal: true });
        setNotice(t('healthHouseConnectors.markInternalSuccess'));
      } else {
        const result = action.token
          ? await rotateHealthHouseConnectorToken(action.connector.id, { token: action.token })
          : await rotateHealthHouseConnectorToken(action.connector.id);
        setNotice(t('healthHouseConnectors.rotateSuccess'));
        setCopyState('');
        setSecret({
          token: result.token,
          code: result.connector?.code || action.connector.code
        });
      }
      await loadConnectors(true);
    } catch (requestError) {
      const fallback = action.type === 'rotate'
        ? t('healthHouseConnectors.rotateError')
        : action.type === 'delete'
          ? t('healthHouseConnectors.deleteError')
          : t('healthHouseConnectors.saveError');
      setError(getRequestErrorMessage(requestError, fallback));
    } finally {
      setBusyId(null);
    }
  };

  const beginRotate = (item) => {
    setError('');
    setNotice('');
    if (item.internal !== true) {
      setConfirmAction({ type: 'rotate', connector: item });
      return;
    }
    setTokenDialog({ connector: item, tokenMode: 'auto', token: '' });
  };

  const prepareRotation = (event) => {
    event.preventDefault();
    if (!tokenDialog) {
      return;
    }
    if (tokenDialog.tokenMode === 'custom' && !isValidToken(tokenDialog.token)) {
      setError(t('healthHouseConnectors.invalidToken'));
      return;
    }
    setConfirmAction({
      type: 'rotate',
      connector: tokenDialog.connector,
      ...(tokenDialog.tokenMode === 'custom' ? { token: tokenDialog.token } : {})
    });
    setTokenDialog(null);
    setError('');
  };

  const closeTokenDialog = () => {
    setTokenDialog(null);
    setError('');
  };

  const copyText = async (value, kind) => {
    let copied = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        copied = true;
      }
    } catch {
      copied = false;
    }
    if (!copied) {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        copied = typeof document.execCommand === 'function' && document.execCommand('copy');
      } catch {
        copied = false;
      } finally {
        document.body.removeChild(textarea);
      }
    }
    setCopyState(copied ? kind : 'failed');
  };

  const closeSecret = () => {
    setSecret(null);
    setCopyState('');
  };

  const renderStatus = (item) => {
    if (item.enabled === false) {
      return <span className="status-pill status-pill--neutral">{t('healthHouseConnectors.disabled')}</span>;
    }
    if (item.online) {
      return <span className="status-pill status-pill--success">{t('healthHouseConnectors.online')}</span>;
    }
    return <span className="status-pill status-pill--danger">{t('healthHouseConnectors.offline')}</span>;
  };

  const confirmDialogCopy = (() => {
    if (confirmAction?.type === 'rotate') {
      return {
        title: t('healthHouseConnectors.rotateConfirmTitle'),
        message: t('healthHouseConnectors.rotateConfirmMessage', { name: confirmAction.connector?.name || '' }),
        confirmText: t('healthHouseConnectors.rotateToken'),
        type: 'warning'
      };
    }
    if (confirmAction?.type === 'mark-internal') {
      return {
        title: t('healthHouseConnectors.markInternalConfirmTitle'),
        message: t('healthHouseConnectors.markInternalConfirmMessage', { name: confirmAction.connector?.name || '' }),
        confirmText: t('healthHouseConnectors.markInternal'),
        type: 'warning'
      };
    }
    if (confirmAction?.type === 'delete') {
      return {
        title: t('healthHouseConnectors.deleteConfirmTitle'),
        message: t('healthHouseConnectors.deleteConfirmMessage', { name: confirmAction.connector?.name || '' }),
        confirmText: t('common.delete'),
        type: 'danger'
      };
    }
    return {
      title: t('healthHouseConnectors.disableConfirmTitle'),
      message: t('healthHouseConnectors.disableConfirmMessage', { name: confirmAction?.connector?.name || '' }),
      confirmText: t('healthHouseConnectors.disable'),
      type: 'danger'
    };
  })();

  const yamlSnippet = secret ? `token: ${secret.token}\nconnector_code: ${secret.code}` : '';

  return (
    <div className="admin-page connector-management-page">
      <div className="page-header admin-header-panel">
        <div className="page-title-block">
          <h1 className="page-title">{t('healthHouseConnectors.title')}</h1>
        </div>
        <div className="page-actions">
          <button type="button" className="theme-button theme-button--primary" onClick={beginCreate}>
            {t('healthHouseConnectors.newConnector')}
          </button>
          <button
            type="button"
            className="theme-button theme-button--ghost"
            onClick={() => loadConnectors(true)}
            disabled={loading || refreshing}
          >
            {refreshing ? t('common.loading') : t('common.refresh')}
          </button>
        </div>
      </div>

      {error && !formDialog && !tokenDialog && (
        <div className="status-banner status-banner--error"><span>{error}</span></div>
      )}
      {notice && (
        <div className="status-banner status-banner--success"><span>{notice}</span></div>
      )}

      <section className="surface-panel admin-panel">
        <div className="surface-panel__body stack">
          <div className="admin-section-header connector-section-header">
            <div className="admin-stat-strip">
              <span className="status-pill status-pill--success">{t('healthHouseConnectors.onlineCount', { count: counts.online })}</span>
              <span className="status-pill status-pill--danger">{t('healthHouseConnectors.offlineCount', { count: counts.offline })}</span>
              <span className="status-pill status-pill--neutral">{t('healthHouseConnectors.disabledCount', { count: counts.disabled })}</span>
            </div>
            <div className="connector-filter-row">
              <label className="field">
                <span className="field__label">{t('common.search')}</span>
                <input
                  className="theme-input"
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t('healthHouseConnectors.searchPlaceholder')}
                />
              </label>
              <label className="field">
                <span className="field__label">{t('healthHouseConnectors.statusFilter')}</span>
                <select className="theme-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                  <option value="all">{t('healthHouseConnectors.allStatuses')}</option>
                  <option value="online">{t('healthHouseConnectors.online')}</option>
                  <option value="offline">{t('healthHouseConnectors.offline')}</option>
                  <option value="disabled">{t('healthHouseConnectors.disabled')}</option>
                </select>
              </label>
            </div>
          </div>

          {loading ? (
            <div className="loading-state"><div className="loading-orb" /><span>{t('common.loading')}</span></div>
          ) : visibleConnectors.length === 0 ? (
            <div className="empty-state">
              <h3>{t('healthHouseConnectors.emptyTitle')}</h3>
              <p>{t('healthHouseConnectors.emptyHint')}</p>
            </div>
          ) : (
            <div className="data-table-shell">
              <table className="data-table connector-table">
                <thead>
                  <tr>
                    <th>{t('healthHouseConnectors.connector')}</th>
                    <th>{t('healthHouseConnectors.status')}</th>
                    <th>{t('healthHouseConnectors.version')}</th>
                    <th>{t('healthHouseConnectors.lastSeen')}</th>
                    <th>{t('healthHouseConnectors.updatedAt')}</th>
                    <th>{t('reports.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleConnectors.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="connector-name-line">
                          <span className="table-title">{item.name}</span>
                          {item.internal && <span className="connector-trust-label">{t('healthHouseConnectors.internal')}</span>}
                        </div>
                        <div className="table-meta connector-code">{item.code}</div>
                        <div className="table-meta">{t('healthHouseConnectors.tokenHint')}: {item.token_hint}</div>
                      </td>
                      <td>{renderStatus(item)}</td>
                      <td>{item.last_version || '-'}</td>
                      <td>{formatDateTime(item.last_seen_at)}</td>
                      <td>{formatDateTime(item.updated_at)}</td>
                      <td>
                        <div className="table-actions connector-actions">
                          <button type="button" className="theme-button theme-button--ghost theme-button--compact" onClick={() => beginEdit(item)} disabled={busyId === item.id}>
                            {t('common.edit')}
                          </button>
                          {!item.internal && (
                            <button type="button" className="theme-button theme-button--ghost theme-button--compact" onClick={() => setConfirmAction({ type: 'mark-internal', connector: item })} disabled={busyId === item.id}>
                              {t('healthHouseConnectors.markInternal')}
                            </button>
                          )}
                          {item.enabled === false ? (
                            <button type="button" className="theme-button theme-button--secondary theme-button--compact" onClick={() => enableConnector(item)} disabled={busyId === item.id}>
                              {t('healthHouseConnectors.enable')}
                            </button>
                          ) : (
                            <button type="button" className="theme-button theme-button--danger theme-button--compact" onClick={() => setConfirmAction({ type: 'disable', connector: item })} disabled={busyId === item.id}>
                              {t('healthHouseConnectors.disable')}
                            </button>
                          )}
                          <button type="button" className="theme-button theme-button--secondary theme-button--compact" onClick={() => beginRotate(item)} disabled={busyId === item.id}>
                            {t('healthHouseConnectors.rotateToken')}
                          </button>
                          <button type="button" className="theme-button theme-button--danger theme-button--compact" onClick={() => setConfirmAction({ type: 'delete', connector: item })} disabled={busyId === item.id}>
                            {t('common.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {formDialog && (
        <DialogPortal>
          <div className="dialog-backdrop">
            <div className="dialog-shell admin-dialog" role="dialog" aria-modal="true" aria-labelledby="connector-form-title">
              <h2 id="connector-form-title" className="dialog-shell__title">
                {t(formDialog.mode === 'create' ? 'healthHouseConnectors.createTitle' : 'healthHouseConnectors.editTitle')}
              </h2>
              {error && <div className="status-banner status-banner--error"><span>{error}</span></div>}
              <form className="stack" onSubmit={handleSubmit}>
                {formDialog.mode === 'create' ? (
                  <label className="field">
                    <span className="field__label" id="connector-code-label">{t('healthHouseConnectors.code')}</span>
                    <input aria-labelledby="connector-code-label" className="theme-input connector-code-input" value={form.code} onChange={(event) => setForm((current) => ({ ...current, code: event.target.value }))} placeholder={t('healthHouseConnectors.codePlaceholder')} autoFocus />
                    <span className="field__hint">{t('healthHouseConnectors.codeHint')}</span>
                  </label>
                ) : (
                  <div className="field">
                    <span className="field__label">{t('healthHouseConnectors.code')}</span>
                    <code className="connector-readonly-code">{form.code}</code>
                    <span className="field__hint">{t('healthHouseConnectors.codeImmutable')}</span>
                  </div>
                )}
                <label className="field">
                  <span className="field__label" id="connector-name-label">{t('healthHouseConnectors.name')}</span>
                  <input aria-labelledby="connector-name-label" className="theme-input" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} maxLength={128} placeholder={t('healthHouseConnectors.namePlaceholder')} autoFocus={formDialog.mode === 'edit'} />
                </label>
                {formDialog.mode === 'create' && (
                  <label className={`connector-trust-toggle ${form.internal ? 'is-active' : ''}`}>
                    <input
                      type="checkbox"
                      checked={form.internal}
                      onChange={(event) => setForm((current) => ({
                        ...current,
                        internal: event.target.checked,
                        tokenMode: 'auto',
                        token: ''
                      }))}
                    />
                    <span>
                      <strong>{t('healthHouseConnectors.internalConnector')}</strong>
                      <small>{t('healthHouseConnectors.internalConnectorHint')}</small>
                    </span>
                  </label>
                )}
                {formDialog.mode === 'create' && form.internal && (
                  <fieldset className="connector-token-options">
                    <legend className="field__label">{t('healthHouseConnectors.tokenSource')}</legend>
                    <div className="connector-token-mode-row">
                      <label>
                        <input type="radio" name="create-token-mode" value="auto" checked={form.tokenMode === 'auto'} onChange={() => setForm((current) => ({ ...current, tokenMode: 'auto', token: '' }))} />
                        <span>{t('healthHouseConnectors.autoToken')}</span>
                      </label>
                      <label>
                        <input type="radio" name="create-token-mode" value="custom" checked={form.tokenMode === 'custom'} onChange={() => setForm((current) => ({ ...current, tokenMode: 'custom' }))} />
                        <span>{t('healthHouseConnectors.customToken')}</span>
                      </label>
                    </div>
                    {form.tokenMode === 'custom' && (
                      <label className="field">
                        <span className="field__label">{t('healthHouseConnectors.token')}</span>
                        <input aria-label={t('healthHouseConnectors.token')} className="theme-input connector-code-input" value={form.token} onChange={(event) => setForm((current) => ({ ...current, token: event.target.value }))} placeholder="test-001" autoComplete="off" spellCheck="false" />
                        <span className="field__hint">{t('healthHouseConnectors.sharedTokenHint')}</span>
                      </label>
                    )}
                  </fieldset>
                )}
                <div className="dialog-shell__actions">
                  <button type="button" className="theme-button theme-button--ghost" onClick={closeForm} disabled={saving}>{t('common.cancel')}</button>
                  <button type="submit" className="theme-button theme-button--primary" disabled={saving}>{saving ? t('common.loading') : t('common.save')}</button>
                </div>
              </form>
            </div>
          </div>
        </DialogPortal>
      )}

      {tokenDialog && (
        <DialogPortal>
          <div className="dialog-backdrop">
            <div className="dialog-shell admin-dialog" role="dialog" aria-modal="true" aria-labelledby="connector-token-title">
              <h2 id="connector-token-title" className="dialog-shell__title">{t('healthHouseConnectors.chooseTokenTitle')}</h2>
              <p className="field__hint">{t('healthHouseConnectors.chooseInternalTokenMessage', { name: tokenDialog.connector.name })}</p>
              {error && <div className="status-banner status-banner--error"><span>{error}</span></div>}
              <form className="stack" onSubmit={prepareRotation}>
                <fieldset className="connector-token-options">
                  <legend className="field__label">{t('healthHouseConnectors.tokenSource')}</legend>
                  <div className="connector-token-mode-row">
                    <label>
                      <input type="radio" name="rotate-token-mode" value="auto" checked={tokenDialog.tokenMode === 'auto'} onChange={() => setTokenDialog((current) => ({ ...current, tokenMode: 'auto', token: '' }))} />
                      <span>{t('healthHouseConnectors.autoToken')}</span>
                    </label>
                    <label>
                      <input type="radio" name="rotate-token-mode" value="custom" checked={tokenDialog.tokenMode === 'custom'} onChange={() => setTokenDialog((current) => ({ ...current, tokenMode: 'custom' }))} />
                      <span>{t('healthHouseConnectors.customToken')}</span>
                    </label>
                  </div>
                  {tokenDialog.tokenMode === 'custom' && (
                    <label className="field">
                      <span className="field__label">{t('healthHouseConnectors.token')}</span>
                      <input aria-label={t('healthHouseConnectors.token')} className="theme-input connector-code-input" value={tokenDialog.token} onChange={(event) => setTokenDialog((current) => ({ ...current, token: event.target.value }))} placeholder="test-001" autoComplete="off" spellCheck="false" autoFocus />
                      <span className="field__hint">{t('healthHouseConnectors.sharedTokenHint')}</span>
                    </label>
                  )}
                </fieldset>
                <div className="dialog-shell__actions">
                  <button type="button" className="theme-button theme-button--ghost" onClick={closeTokenDialog}>{t('common.cancel')}</button>
                  <button type="submit" className="theme-button theme-button--primary">{t('healthHouseConnectors.continueToConfirm')}</button>
                </div>
              </form>
            </div>
          </div>
        </DialogPortal>
      )}

      {secret && (
        <DialogPortal>
          <div className="dialog-backdrop">
            <div className="dialog-shell dialog-shell--medium admin-dialog connector-secret-dialog" role="dialog" aria-modal="true" aria-labelledby="connector-secret-title">
              <h2 id="connector-secret-title" className="dialog-shell__title">{t('healthHouseConnectors.oneTimeTitle')}</h2>
              <div className="status-banner status-banner--warning"><span>{t('healthHouseConnectors.oneTimeWarning')}</span></div>
              <div className="connector-secret-block">
                <div className="connector-secret-heading">
                  <span>{t('healthHouseConnectors.token')}</span>
                  <button type="button" className="theme-button theme-button--ghost theme-button--compact" onClick={() => copyText(secret.token, 'token')}>
                    {t('healthHouseConnectors.copyToken')}
                  </button>
                </div>
                <code data-testid="one-time-token">{secret.token}</code>
              </div>
              <div className="connector-secret-block">
                <div className="connector-secret-heading">
                  <span>{t('healthHouseConnectors.yamlConfig')}</span>
                  <button type="button" className="theme-button theme-button--ghost theme-button--compact" onClick={() => copyText(yamlSnippet, 'yaml')}>
                    {t('healthHouseConnectors.copyYaml')}
                  </button>
                </div>
                <pre>{yamlSnippet}</pre>
              </div>
              {copyState && (
                <p className={`connector-copy-state ${copyState === 'failed' ? 'is-error' : ''}`}>
                  {t(copyState === 'failed' ? 'healthHouseConnectors.copyFailed' : 'healthHouseConnectors.copySuccess')}
                </p>
              )}
              <div className="dialog-shell__actions">
                <button type="button" className="theme-button theme-button--primary" onClick={closeSecret}>
                  {t('healthHouseConnectors.savedAndClose')}
                </button>
              </div>
            </div>
          </div>
        </DialogPortal>
      )}

      <ConfirmDialog
        isOpen={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        onConfirm={runConfirmedAction}
        title={confirmDialogCopy.title}
        message={confirmDialogCopy.message}
        confirmText={confirmDialogCopy.confirmText}
        type={confirmDialogCopy.type}
      />
    </div>
  );
};

export default HealthHouseConnectors;
